import { call, put, takeLatest } from "redux-saga/effects";
import axios, { AxiosError, AxiosResponse } from "axios";
import JSZip from "jszip";
import moment from "moment";

import { errorToast } from "data-handler/ducks/toast";
import { toastFormattedMessages } from "SCConstants";
import { downloadBlob } from "data-handler/ducks/utils";
import { RootState } from "data-handler/rootReducer";

import { Filters } from "types";

const DOWNLOAD_DATASET_REQUEST =
  "schoolconnect/datasets/DOWNLOAD_DATASET_REQUEST";
const DOWNLOAD_DATASET_SUCCESS =
  "schoolconnect/datasets/DOWNLOAD_DATASET_SUCCESS";
const DOWNLOAD_DATASET_FAILURE =
  "schoolconnect/datasets/DOWNLOAD_DATASET_FAILURE";
const DOWNLOAD_DATASETS_REQUEST =
  "schoolconnect/datasets/DOWNLOAD_DATASETS_REQUEST";
const DOWNLOAD_DATASETS_SUCCESS =
  "schoolconnect/datasets/DOWNLOAD_DATASETS_SUCCESS";
const DOWNLOAD_DATASETS_FAILURE =
  "schoolconnect/datasets/DOWNLOAD_DATASETS_FAILURE";

type State = {
  fetching: boolean;
  error: any;
  errorResponse: any;
};

type Dataset = "stock_movement";

type DownloadDatasetRequestAction = {
  type: typeof DOWNLOAD_DATASET_REQUEST;
  dataset: Dataset;
  filters: Filters;
};

type DownloadDatasetSuccessAction = {
  type: typeof DOWNLOAD_DATASET_SUCCESS;
};

type DownloadDatasetFailureAction = {
  type: typeof DOWNLOAD_DATASET_FAILURE;
  error: AxiosError;
};

type DownloadDatasetsRequestAction = {
  type: typeof DOWNLOAD_DATASETS_REQUEST;
  datasets: string[];
  filters: Filters;
};

type DownloadDatasetsSuccessAction = {
  type: typeof DOWNLOAD_DATASETS_SUCCESS;
};

type DownloadDatasetsFailureAction = {
  type: typeof DOWNLOAD_DATASETS_FAILURE;
  error: AxiosError;
};

type Action =
  | DownloadDatasetRequestAction
  | DownloadDatasetSuccessAction
  | DownloadDatasetFailureAction
  | DownloadDatasetsRequestAction
  | DownloadDatasetsSuccessAction
  | DownloadDatasetsFailureAction;

const initialState: State = {
  fetching: false,
  error: null,
  errorResponse: null,
};

export default function reducer(state = initialState, action: Action): State {
  switch (action.type) {
    case DOWNLOAD_DATASET_REQUEST:
    case DOWNLOAD_DATASETS_REQUEST:
      return { ...state, fetching: true, error: null };
    case DOWNLOAD_DATASET_SUCCESS:
    case DOWNLOAD_DATASETS_SUCCESS:
      return {
        ...state,
        error: null,
        fetching: false,
      };
    case DOWNLOAD_DATASET_FAILURE:
    case DOWNLOAD_DATASETS_FAILURE:
      return {
        ...state,
        fetching: false,
        error: action.error,
        errorResponse: action.error.response,
      };
    default:
      return state;
  }
}

// Action
export const downloadDataset = (dataset: Dataset, filters: Filters) => ({
  type: DOWNLOAD_DATASET_REQUEST,
  dataset,
  filters,
});

// Saga
export function* downloadDatasetSagaWatcher() {
  yield takeLatest(DOWNLOAD_DATASET_REQUEST, downloadDatasetSagaWorker);
}

function buildFiltersQueryString(filters: Filters): string {
  if (!filters || !filters.length) return "";
  return filters
    .map((filter) => {
      const id = filter.id;
      const value = filter.value;
      if (value && value !== "") {
        // Split comma-separated values into repeated params
        // (e.g. adm_1=1,2 -> &adm_1=1&adm_1=2) so the backend's
        // query_params.getlist() picks them up correctly.
        return value
          .split(",")
          .map((v) => `&${id}=${v.trim()}`)
          .join("");
      }
      return "";
    })
    .join("");
}

function fetchDataset(action: DownloadDatasetRequestAction) {
  const { dataset, filters } = action;

  const url =
    `${process.env.REACT_APP_API_URL}/datasets/${dataset}/?` +
    buildFiltersQueryString(filters);

  return axios({
    method: "GET",
    url: url,
    responseType: "blob",
  });
}

function* downloadDatasetSagaWorker(action: DownloadDatasetRequestAction) {
  try {
    const { dataset } = action;
    const response: AxiosResponse = yield call(fetchDataset, action);
    const { data: blob } = response;
    downloadBlob(blob, `${dataset.toUpperCase()}.csv`);
    yield put({ type: DOWNLOAD_DATASET_SUCCESS });
  } catch (error) {
    yield put({ type: DOWNLOAD_DATASET_FAILURE, error });
    yield put(
      errorToast(
        toastFormattedMessages.find((e) => e.name === "toast.infomationError")
          ?.label
      )
    );
  }
}

export const getDatasetsIsFetching = (state: RootState) =>
  state.datasets.fetching;

// Action (plural - downloads multiple datasets as a zip)
export const downloadDatasets = (datasets: string[], filters: Filters) => ({
  type: DOWNLOAD_DATASETS_REQUEST,
  datasets,
  filters,
});

// Saga (plural)
export function* downloadDatasetsSagaWatcher() {
  yield takeLatest(DOWNLOAD_DATASETS_REQUEST, downloadDatasetsSagaWorker);
}

function buildDatasetUrl(dataset: string, filters: Filters) {
  return (
    `${process.env.REACT_APP_API_URL}/datasets/${dataset}/?` +
    buildFiltersQueryString(filters)
  );
}

function fetchDatasetByUrl(dataset: string, filters: Filters) {
  return axios({
    method: "GET",
    url: buildDatasetUrl(dataset, filters),
    responseType: "blob",
  });
}

function* downloadDatasetsSagaWorker(action: DownloadDatasetsRequestAction) {
  try {
    const { datasets, filters } = action;

    if (datasets.length === 1) {
      const response: AxiosResponse = yield call(
        fetchDatasetByUrl,
        datasets[0],
        filters
      );
      downloadBlob(response.data, `${datasets[0].toUpperCase()}.csv`);
    } else {
      const zip = new JSZip();
      for (const dataset of datasets) {
        const response: AxiosResponse = yield call(
          fetchDatasetByUrl,
          dataset,
          filters
        );
        const text: string = yield call(
          async () => await (response.data as Blob).text()
        );
        zip.file(`${dataset.toUpperCase()}.csv`, text);
      }
      const blob: Blob = yield call(
        async () => await zip.generateAsync({ type: "blob" })
      );
      const months = filters
        .filter((f) => f.id === "months")
        .map((f) => f.value)
        .sort();
      const start = months.length
        ? moment(months[0] + "-01")
            .format("MMM-YYYY")
            .toLowerCase()
        : "all";
      const end = months.length
        ? moment(months[months.length - 1] + "-01")
            .format("MMM-YYYY")
            .toLowerCase()
        : "all";
      downloadBlob(blob, `${start}-${end}-datasets.zip`);
    }

    yield put({ type: DOWNLOAD_DATASETS_SUCCESS });
  } catch (error) {
    yield put({ type: DOWNLOAD_DATASETS_FAILURE, error });
    yield put(
      errorToast(
        toastFormattedMessages.find((e) => e.name === "toast.infomationError")
          ?.label
      )
    );
  }
}
